import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {Store} from '@ngrx/store';
import {AppState} from '@store/state/app.state';
import {EnvironmentService} from '@iw-shop/core/services/environment.service';
import {catchError, filter, finalize, switchMap, take, tap, timeout} from 'rxjs/operators';
import {EMPTY, Observable, of, TimeoutError, first} from 'rxjs';
import {
    ApiShoppingCartsDeleteRequest,
    ApiShoppingCartsDeleteResponse,
    ApiShoppingCartsGetRequest,
    ApiShoppingCartsGetResponse, ApiShoppingCartsSetRequest, ApiShoppingCartsSetResponse,
    ApiShoppingCartsUpdateRequest, ApiShoppingCartsUpdateResponse
} from '@api/shopping-carts';
import {
    getSelectedBillingAddress,
    getSelectedCustomer,
    getSelectedDeliveryAddress
} from '@store/selectors/user.selector';
import {DeliveryTourService} from "@iw-shop/delivery-tour/services/delivery-tour.service";
import {increaseRequestsCounter, increaseRequestsDone} from "@store/actions/shopping-cart.action";
import {shoppingCart} from "@store/actions/storage.action";
import {ConfigService} from "@iw-shop/config/service/config.service";

@Injectable({
    providedIn: 'root'
})
export class ShoppingCartService {


    constructor(private http: HttpClient, private store: Store<AppState>, private environmentService: EnvironmentService, private configService: ConfigService) {

    }

    getShoppingCartRequestsCounter(): number {
        let value: number = 0;
        this.store.select(state => state.shoppingCart).pipe(take(1))
            .subscribe(
                r => {
                    if (r.requestsCounter) {
                        value = r.requestsCounter;
                    }
                }
            );
        return value;
    }

    getShoppingCartId(): string | undefined {
        let value: string | undefined = undefined;
        this.store.select(state => state.storage).pipe(take(1)).subscribe(
            s => {
                if (s.shoppingCart?.id) {
                    value = s.shoppingCart.id;
                }
            }
        )
        return value;
    }

    getShoppingCartToken(): string | undefined {
        let value: string | undefined = undefined;
        this.store.select(state => state.storage).pipe(take(1)).subscribe(
            s => {
                if (s.shoppingCart?.token) {
                    value = s.shoppingCart?.token;
                }
            }
        )
        return value;
    }

    getDefaultParams(getParams: ['customerId', 'country'] = ['customerId', 'country']): {
        customerId?: string,
        country?: string
    } {
        let params: { customerId?: string, country?: string } = {};
        if (getParams.filter(r => r == 'customerId')) {
            this.store.select(state => state.config).pipe(take(1)).subscribe(r => {
                params.country = r.defaultCountry
            });
            this.store.select(getSelectedBillingAddress).pipe(take(1)).subscribe(r => {
                if (r?.country) {
                    params.country = r.country;
                }
            });
            this.store.select(getSelectedDeliveryAddress).pipe(take(1)).subscribe(r => {
                if (r?.country) {
                    params.country = r.country;
                }
            });
        }
        if (getParams.filter(r => r == 'country')) {
            this.store.select(getSelectedCustomer).pipe(take(1)).subscribe(r => {
                if (r) {
                    params.customerId = r.id
                }
            })
        }
        return params;
    }


    getDeselectedDynamicArticles(): { amount: number, id: string }[] {
        let value: { amount: number, id: string }[] = [];
        this.store.select(state => state.storage).pipe(
            take(1),
        ).subscribe(
            r => {
                r.shoppingCart?.dynamicArticleIds?.deselected?.map(
                    id => {
                        value.push({amount: 0, id})
                    }
                )
            }
        );
        return value;
    }


    getShoppingCart(id: string, token: string): Observable<ApiShoppingCartsGetResponse> {
        const body: ApiShoppingCartsGetRequest = {
            id: 'getShoppingCartArticle',
            command: 'shoppingCarts/get',
            params: {
                filters: [
                    {fields: ['id'], values: [id]},
                    {fields: ['token'], values: [token]}
                ],
                shoppingCarts: {}
            }
        };
        return this.http.post<ApiShoppingCartsGetResponse>(this.environmentService.environment.urlApi, JSON.stringify(body));
    }

    setRequestsDone() {
        this.store.dispatch(increaseRequestsDone());
    }

    setShoppingCart(
        jsonId: string,
        params: Partial<ApiShoppingCartsSetRequest['params']>
    ): Observable<ApiShoppingCartsSetResponse | ApiShoppingCartsUpdateResponse> {


        let copyOfRequestCounter = this.getShoppingCartRequestsCounter();
        this.store.dispatch(increaseRequestsCounter());
        return this.store.select(state => state.shoppingCart).pipe(
            first(state => (state.requestsDone == (copyOfRequestCounter))),
            timeout(1000),
            catchError(error => {
                if (error instanceof TimeoutError) {
                    // Handle timeout error here, for example
                    console.error('Timeout has occurred');
                    return of(null);
                } else {
                    // Rethrow the error if it's not a timeout
                    throw error;
                }
            }),
            take(1),
            switchMap(r => {
                console.log('SC: setShoppingCart');
                if (this.getShoppingCartId()) {
                    const body: ApiShoppingCartsUpdateRequest = {
                        id: jsonId,
                        command: 'shoppingCarts/update',
                        params: {
                            data: {
                                ...params.data,
                                ...this.getDefaultParams(),
                                id: this.getShoppingCartId() ?? '',
                                token: this.getShoppingCartToken() ?? ''
                            }
                        }
                    };
                    if (params.priceGroups) {
                        body.params.priceGroups = params.priceGroups;
                    }
                    return this.http.post<ApiShoppingCartsUpdateResponse>(this.environmentService.environment.urlApi, JSON.stringify(body)).pipe(
                        tap(
                            () => {
                                console.log('SC: setShoppingCart #update done');
                                this.setRequestsDone()
                            }
                        ),
                        catchError(err => {
                            this.setRequestsDone();
                            console.log('Handling error locally and rethrowing it...', err);
                            throw err;
                        })
                    );
                } else {
                    const body: ApiShoppingCartsSetRequest = {
                        id: jsonId,
                        command: 'shoppingCarts/set',
                        params: {
                            data: {
                                ...params.data,
                                ...this.getDefaultParams(),
                            }
                        }
                    };
                    if (params.priceGroups) {
                        body.params.priceGroups = params.priceGroups;
                    }
                    return this.http.post<ApiShoppingCartsSetResponse>(this.environmentService.environment.urlApi, JSON.stringify(body)).pipe(

                        tap(
                            (r) => {
                                this.store.dispatch(shoppingCart({
                                    id: r.result?.shoppingCarts.data.id ?? '',
                                    token: r.result?.shoppingCarts.data.token ?? ''
                                }));
                            }
                        ),
                        catchError(err => {
                            this.setRequestsDone();
                            console.log('Handling error locally and rethrowing it...', err);
                            throw err;
                        }),
                        finalize(() => {
                            this.setRequestsDone();
                            console.log('SC: setShoppingCart counter cleaned up');
                        })
                    );
                }

            })
        );
    }


    updateStatus(id: string, token: string, status: 'paymentPending' | null): Observable<ApiShoppingCartsUpdateResponse> {
        const body: ApiShoppingCartsUpdateRequest = {
            id: 'updateShoppingCartStatus',
            command: 'shoppingCarts/update',
            params: {
                data: {
                    id,
                    token,
                    status
                }
            }
        };

        let copyOfRequestCounter = this.getShoppingCartRequestsCounter();
        this.store.dispatch(increaseRequestsCounter());
        return this.store.select(state => state.shoppingCart).pipe(
            first(state => (state.requestsDone == (copyOfRequestCounter))),
            timeout(1000),
            catchError(error => {
                if (error instanceof TimeoutError) {
                    // Handle timeout error here, for example
                    console.error('Timeout has occurred');
                    return of(null);
                } else {
                    // Rethrow the error if it's not a timeout
                    throw error;
                }
            }),
            take(1),
            switchMap(r => {
                console.log('SC: updateStatus')
                body.params.data = {...body.params.data, ...this.getDefaultParams()};
                return this.http.post<ApiShoppingCartsUpdateResponse>(this.environmentService.environment.urlApi, JSON.stringify(body)).pipe(
                    finalize(() => {
                        console.log('SC: updateStatus done');
                        this.setRequestsDone()
                    }),
                    catchError(err => {
                        this.setRequestsDone();
                        console.log('Handling error locally and rethrowing it...', err);
                        throw err;
                    })
                );
            })
        );


    }

    isDynamicArticleInCart(id: string): boolean {
        let selected = false;
        this.store.select(state => state.shoppingCart)
            .pipe(take(1))
            .subscribe(r => {
                if (r) {
                    let dynamicArticle = r.items?.dynamicArticles.filter(x => x.id == id)[0];
                    if (dynamicArticle && dynamicArticle.amount != 0) {
                        selected = true;
                    }
                }


            });
        return selected;
    }


    refreshShoppingCartArticles(jsonId: string, priceGroups?: string[], deliveryDate?: string): Observable<ApiShoppingCartsUpdateResponse> {
        let articles: { amount: number, articleNumber: string }[] = [];

        let copyOfRequestCounter = this.getShoppingCartRequestsCounter();
        this.store.dispatch(increaseRequestsCounter());
        return this.store.select(state => state.shoppingCart).pipe(
            first(state => (state.requestsDone == (copyOfRequestCounter))),
            timeout(10000),
            catchError(error => {
                if (error instanceof TimeoutError) {
                    // Handle timeout error here, for example
                    console.error('Timeout has occurred');
                    return of(null);
                } else {
                    // Rethrow the error if it's not a timeout
                    throw error;
                }
            }),
            take(1),
            switchMap(r => {
                console.log('SC: refreshShoppingCartArticles');
                this.store.select(state => state.shoppingCart)
                    .pipe(
                        take(1)
                    )
                    .subscribe(
                        res => {
                            if (res.items) {
                                res.items.articles.map(
                                    article => {
                                        articles.push({amount: article.amount, articleNumber: article.articleNumber})
                                    }
                                )
                            }
                        }
                    )

                let data: Partial<ApiShoppingCartsSetRequest['params']['data']> = {articles};

                if (deliveryDate) {
                    data.deliveryDate = deliveryDate;
                }

                return this.setShoppingCart(
                    jsonId,
                    {
                        priceGroups,
                        data
                    }
                ).pipe(
                    finalize(() => {
                        this.setRequestsDone();
                        console.log('SC: refreshShoppingCartArticles done', jsonId);
                    }),
                    catchError(err => {
                        this.setRequestsDone();
                        console.log('Handling error locally and rethrowing it...', err);
                        throw err;
                    })
                );
            })
        );


    }

    deleteShoppingCart(id: string, token: string): Observable<ApiShoppingCartsDeleteResponse> {

        console.log('call deleteShoppingCart counter'+this.getShoppingCartRequestsCounter());
        let copyOfRequestCounter = this.getShoppingCartRequestsCounter();
        this.store.dispatch(increaseRequestsCounter());
        return this.store.select(state => state.shoppingCart).pipe(
            first(state => (state.requestsDone == (copyOfRequestCounter))),
            timeout(10000),
            catchError(error => {
                if (error instanceof TimeoutError) {
                    // Handle timeout error here, for example
                    console.error('Timeout has occurred');
                    return of(null);
                } else {
                    // Rethrow the error if it's not a timeout
                    throw error;
                }
            }),
            take(1),
            switchMap(r => {
                console.log('SC: start request to deleteShoppingCart'+this.getShoppingCartRequestsCounter());
                const body: ApiShoppingCartsDeleteRequest = {
                    id: 'deleteShoppingCart',
                    command: 'shoppingCarts/delete',
                    params: {
                        data: {
                            id,
                            token
                        }
                    }
                };

                return this.http.post<ApiShoppingCartsDeleteResponse>(this.environmentService.environment.urlApi, JSON.stringify(body)).pipe(
                    finalize(() => {
                        this.setRequestsDone();
                        console.log('SC: deleteShoppingCart done');
                    }),
                    catchError(err => {
                        this.setRequestsDone();
                        console.log('Handling error locally and rethrowing it...', err);
                        throw err;
                    })
                );
            })
        );
    }
}
